import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react'

// @mui material components
import Card from '@mui/material/Card'

// Soft UI Dashboard PRO React components
import SoftBox from 'components/SoftBox'
import SoftTypography from 'components/SoftTypography'
import SoftSelect from 'components/SoftSelect'
import { Box, MenuItem, Select, Typography } from '@mui/material'

// Soft UI Dashboard PRO React icons
import SpaceShip from 'components/Icons/SpaceShip'
import {
  AccountBox,
  BeachAccess,
  Favorite,
  HealthAndSafety,
  LocalHospital,
  MilitaryTech,
  Paid,
  People,
  Receipt,
  RocketLaunch,
  Spa,
  Terrain,
  TrendingUp,
  Work,
} from '@mui/icons-material'

const Sidenav = forwardRef(({ isMobile }, ref) => {
  const [scrollPosition, setScrollPosition] = useState(0)
  const [tabYValues, setTabYValues] = useState([])
  const [currentTab, setCurrentTab] = useState('basic-info')

  const updateTabLocations = () => {
    // console.log('Updating Tab Locations...')
    const tabs = document.getElementsByClassName('settings-tab')
    const newTabYValues = []

    Array.from(tabs).forEach((tab) => {
      const rect = tab.getBoundingClientRect()
      const tabYPosition = rect.top + window.scrollY
      newTabYValues.push({ href: tab.id, y: tabYPosition })
    })

    setTabYValues(newTabYValues)
  }

  //allows us to call updateTabLocations from the profile page
  //called when page layout changes
  useImperativeHandle(ref, () => ({
    updateTabLocations,
  }))

  useEffect(() => {
    const handleScroll = () => {
      const position = window.scrollY
      setScrollPosition(position)

      const isAtBottom =
        position + window.innerHeight >=
        document.documentElement.scrollHeight - 50

      requestAnimationFrame(() => {
        tabYValues.forEach((tab, index) => {
          const isLastTab = index === tabYValues.length - 1

          if (isLastTab) {
            if (position >= tab.y - 100 && position <= tab.y + 100) {
              setCurrentTab(tab.href)
            }
          } else {
            if (position >= tab.y - 150 && position <= tab.y + 150) {
              setCurrentTab(tab.href)
            }
          }
        })

        if (isAtBottom && tabYValues.length) {
          setCurrentTab(tabYValues[tabYValues.length - 1].href)
        }
      })
    }

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [scrollPosition, tabYValues])

  const scrollToSection = (event) => {
    const selectedTab = event.target.value

    const targetSection = document.getElementById(selectedTab)
    if (targetSection) {
      targetSection.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })
    }
  }

  const sidenavItems = [
    { label: 'Quick Navigation', href: 'quick', icon: <RocketLaunch /> },
    { label: 'Personal', href: 'basic-info', icon: <AccountBox /> },
    { label: 'Employment', href: 'employment', icon: <Work /> },
    { label: 'Military', href: 'military', icon: <MilitaryTech /> },
    { label: 'Family', href: 'family', icon: <People /> },
    { label: 'Leave & Earnings', href: 'paystub', icon: <Paid /> },
    { label: 'Deductions', href: 'deductions', icon: <Receipt /> },
    {
      label: 'Health Insurance',
      href: 'healthcare',
      icon: <HealthAndSafety />,
    },
    {
      label: 'FEGLI & Life Insurance',
      href: 'lifeinsurance',
      icon: <LocalHospital />,
    },
    { label: 'Retirement', href: 'retirement', icon: <BeachAccess /> },
    { label: 'TSP', href: 'tsp', icon: <Terrain /> },
    { label: 'Social Security', href: 'socialsecurity', icon: <Favorite /> },
    { label: 'Other', href: 'other', icon: <Spa /> },
  ]

  const renderSidenavItems = sidenavItems.map(({ icon, label, href }, key) => {
    const itemKey = `item-${key}`

    return (
      <Box key={itemKey}>
        <SoftTypography
          component="a"
          href={`#${href}`}
          variant="button"
          fontWeight="regular"
          color="text"
          textTransform="capitalize"
          sx={({
            borders: { borderRadius },
            functions: { pxToRem },
            palette: { light },
            transitions,
          }) => ({
            display: 'flex',
            alignItems: 'center',
            borderRadius: borderRadius.md,
            backgroundColor: currentTab === href ? light.main : 'none',
            fontWeight: currentTab === href ? 'bold' : 'regular',
            padding: `${pxToRem(10)} ${pxToRem(0)}`,
            transition: transitions.create('background-color', {
              easing: transitions.easing.easeInOut,
              duration: transitions.duration.shorter,
            }),

            '&:hover': {
              backgroundColor: light.main,
              fontWeight: 'bold',
            },
          })}
        >
          <Box mr={1.5} pl={1.5} lineHeight={1} sx={{ fontSize: '20px' }}>
            {icon}
          </Box>
          {label}
        </SoftTypography>
      </Box>
    )
  })

  return (
    <>
      {isMobile ? (
        <Select
          MenuProps={{
            style: {
              maxHeight: 350,
            },
          }}
          sx={{
            padding: '0.5rem 0.75rem !important',
            '&>div.MuiSelect-select': {
              width: '100% !important',
              padding: '0 !important',
            },
            '&>svg': {
              fontSize: '1.5rem !important',
              display: 'inline-block',
            },
          }}
          value={'quick'}
          onChange={scrollToSection}
        >
          {sidenavItems.map(({ label, href, icon }, key) => (
            <MenuItem key={key} value={href}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  fontSize: '18px',
                }}
              >
                {icon ? icon : <></>}

                <Typography
                  ml={1}
                  fontWeight="semi-bold"
                  color="text"
                  variant="body2"
                  fontSize="18px"
                >
                  {label}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Card
          sx={{
            borderRadius: '8px',
            position: 'sticky',
            top: 100,
            border: '1px solid #ddd',
            boxShadow: 0,
          }}
        >
          <Box
            display="flex"
            flexDirection="column"
            p={2}
            // py={1}
            m={0}
            sx={{ listStyle: 'none' }}
          >
            {renderSidenavItems.slice(1, renderSidenavItems.length)}
          </Box>
        </Card>
      )}
    </>
  )
})

Sidenav.displayName = 'Sidenav'

export default Sidenav
